import { useState } from 'react'
import { LockKey } from '@phosphor-icons/react'
import { isApiError } from '@/api'
import { useAuth } from '@/auth/context'
import { cn } from '@/lib/cn'
import { Banner, Button, Field, Input, Modal, SkeletonText, TextArea } from '@/ui'
import { errorDetail, errorMessage } from '../../api/core'
import { obj } from '../../api/mappers'
import { reauthenticate } from '../../api/reauth'
import { useDecideRelease, usePackLot, useReleaseChecklist } from '../../api/lab'
import {
  HOLD_REASON_MAX,
  checklistChanges,
  emptyReleaseDecision,
  isOwnRun,
  releasableState,
  releaseBlockedReason,
  releaseDecisionBody,
  releaseDecisionOk,
  validateReleaseDecision,
  type ReleaseDecision,
  type ReleaseDecisionDraft,
} from '../../model/lab/releaseDecisionForm'
import { readableDetail } from '../../model/lab/labText'
import { Code } from '../bits'
import { ReleaseChecklistView } from './ReleaseChecklist'

const PASSWORD_MISMATCH = 'Password did not match'

/**
 * Release or hold a pack lot (F7.6-c): the checklist, "I attest", the signer's own password.
 * Mount it only while open so every opening starts clean.
 */
export function ReleaseDecisionDialog({
  packLotId,
  initialDecision = 'RELEASE',
  onClose,
}: {
  packLotId: string
  initialDecision?: ReleaseDecision
  onClose: () => void
}) {
  const { user } = useAuth()
  const lot = usePackLot(packLotId)
  const checklist = useReleaseChecklist(packLotId)
  const decide = useDecideRelease()
  const [d, setD] = useState<ReleaseDecisionDraft>(() => emptyReleaseDecision(initialDecision))
  const [touched, setTouched] = useState(false)
  const [signing, setSigning] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [changed, setChanged] = useState<ReturnType<typeof checklistChanges>>([])

  const ownRun = isOwnRun(user?.id, lot.data ?? null)
  const canRelease = checklist.data?.canRelease ?? false
  const ctx = { ownRun, canRelease, releasable: lot.data ? releasableState(lot.data.status, lot.data.hold) : undefined }
  const releaseBlocked = releaseBlockedReason(ctx)
  const ready = !lot.isLoading && !checklist.isLoading
  // Release cannot be chosen while it is blocked: the dialog then signs a hold.
  const decision: ReleaseDecision = d.decision === 'RELEASE' && releaseBlocked && ready ? 'HOLD' : d.decision
  const p = validateReleaseDecision({ ...d, decision }, ctx)
  const ok = releaseDecisionOk(p)
  const loading = signing || decide.isPending

  const submit = async () => {
    setTouched(true)
    setProblem(null)
    setPasswordError(null)
    if (!ok) return
    setSigning(true)
    let token: string
    try {
      token = await reauthenticate(d.password)
    } catch (e) {
      setSigning(false)
      if (isApiError(e) && (e.status === 400 || e.status === 401 || e.status === 403)) setPasswordError(PASSWORD_MISMATCH)
      else setProblem(errorMessage(e))
      setD((x) => ({ ...x, password: '' }))
      return
    }
    try {
      await decide.mutateAsync({ packLotId, body: releaseDecisionBody({ ...d, decision }, token) })
      onClose()
    } catch (e) {
      const code = isApiError(e) ? e.code : undefined
      if (code === 'CHECKLIST_CHANGED') {
        setChanged(checklistChanges(obj(errorDetail(e).errors).failing))
        await checklist.refetch()
      } else if (code === 'REAUTH_REQUIRED' || (isApiError(e) && e.status === 401)) {
        setPasswordError(PASSWORD_MISMATCH)
      } else {
        setProblem(errorMessage(e))
      }
      setD((x) => ({ ...x, password: '' }))
    } finally {
      setSigning(false)
    }
  }

  const choose = (decision: ReleaseDecision) => setD((x) => ({ ...x, decision }))

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!loading}
      title={
        <span>
          Release decision{lot.data && <> · <Code className="text-[inherit]">{lot.data.code}</Code></>}
        </span>
      }
      description={lot.data ? `${lot.data.skuName ?? ''} · ${lot.data.pouchCount ?? '—'} pouches` : undefined}
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={decision === 'HOLD' ? 'danger' : 'primary'}
            leadingIcon={<LockKey />}
            loading={loading}
            disabled={!ready || (decision === 'RELEASE' && Boolean(releaseBlocked))}
            onClick={() => void submit()}
          >
            {decision === 'RELEASE' ? 'Sign and release' : 'Sign hold'}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        {!ready ? (
          <SkeletonText lines={6} />
        ) : checklist.isError || lot.isError ? (
          <Banner tone="error" title="Could not load the checklist">
            {errorMessage(checklist.error ?? lot.error)}
          </Banner>
        ) : (
          <>
            {changed.length > 0 && (
              <Banner tone="warn" title="The checklist changed since you opened it">
                <ul className="mt-1 flex flex-col gap-1 text-ink">
                  {changed.map((c) => (
                    <li key={c.key} className="text-[13.5px]">
                      <span className="font-semibold">{c.label}</span>
                      {c.detail.length > 0 && <>: {c.detail.map(readableDetail).join('; ')}</>}
                    </li>
                  ))}
                </ul>
              </Banner>
            )}
            {checklist.data && (
              <div className="rounded-card border border-rule px-3 py-1">
                <ReleaseChecklistView checklist={checklist.data} highlight={changed.map((c) => c.key)} compact />
              </div>
            )}

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-[14px] font-semibold text-ink">Decision</legend>
              <div className="grid grid-cols-2 gap-2">
                {(['RELEASE', 'HOLD'] as const).map((k) => {
                  const blocked = k === 'RELEASE' ? releaseBlocked : null
                  return (
                    <label
                      key={k}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-chip border px-3 py-2 text-[14px]',
                        decision === k ? 'border-ink bg-paper-tint' : 'border-rule',
                        blocked && 'cursor-not-allowed opacity-60',
                      )}
                      title={blocked ?? undefined}
                    >
                      <input
                        type="radio"
                        name="decision"
                        value={k}
                        checked={decision === k}
                        disabled={Boolean(blocked)}
                        onChange={() => choose(k)}
                      />
                      {k === 'RELEASE' ? 'Release for sale' : 'Hold'}
                    </label>
                  )
                })}
              </div>
              {releaseBlocked && <p className="text-[13px] text-muted">{releaseBlocked}</p>}
            </fieldset>

            {decision === 'HOLD' && (
              <Field
                label="Why is it held?"
                required
                error={touched ? p.reason : undefined}
                hint="At least 10 characters. The person who lifts the hold reads this."
              >
                <TextArea
                  value={d.reason}
                  maxLength={HOLD_REASON_MAX}
                  onChange={(e) => setD({ ...d, reason: e.target.value })}
                  rows={3}
                  data-autofocus
                />
              </Field>
            )}

            <label className="flex items-start gap-2 text-[14px]">
              <input
                type="checkbox"
                className="mt-1"
                checked={d.attested}
                onChange={(e) => setD({ ...d, attested: e.target.checked })}
              />
              <span>
                I have read the lab report and checked the label.
                {touched && p.attested && <span className="block text-[13px] font-medium text-danger">{p.attested}</span>}
              </span>
            </label>

            <Field label="Your password" required error={passwordError ?? (touched ? p.password : undefined)} hint="Signing needs your own login password.">
              <Input
                type="password"
                autoComplete="current-password"
                value={d.password}
                onChange={(e) => {
                  setPasswordError(null)
                  setD({ ...d, password: e.target.value })
                }}
              />
            </Field>

            {problem && (
              <Banner tone="error" title="Not signed">
                {problem}
              </Banner>
            )}
          </>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  )
}
